import { useEffect, useMemo, useState } from "react";
import { useInfiniteQuery } from "react-query";
import { AxiosResponse } from "axios";
import { QueryHandler } from "../network/models/QueryHandlers";
import { InfiniteListControls } from "./infiniteListTypes";
import {
  DEFAULT_SEARCH_DEBOUNCE_MS,
  DEFAULT_SEARCH_PAGE_SIZE,
} from "../constants/search";

type UseInfiniteSearchListOptions = {
  pageSize?: number;
  debounceMs?: number;
  enabled?: boolean;
};

type UseInfiniteSearchListParams<TItem> = {
  searchTermQuery?: string;
  queryKey: unknown[];
  handlers?: QueryHandler<any | undefined, AxiosResponse<any>>;
  options?: UseInfiniteSearchListOptions;
  fetchPage: (args: {
    pageParam: number;
    pageSize: number;
    debouncedSearchTerm: string;
    signal?: AbortSignal;
  }) => Promise<TItem[]>;
};

export const useInfiniteSearchList = <TItem>({
  searchTermQuery = "",
  queryKey,
  handlers,
  options = {},
  fetchPage,
}: UseInfiniteSearchListParams<TItem>): [
  TItem[] | undefined,
  (searchTermQuery: string) => void,
  InfiniteListControls,
] => {
  const pageSize = options.pageSize ?? DEFAULT_SEARCH_PAGE_SIZE;
  const debounceMs = options.debounceMs ?? DEFAULT_SEARCH_DEBOUNCE_MS;
  const enabled = options.enabled ?? true;
  const [searchTerm, setSearchTerm] = useState(searchTermQuery);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTermQuery);

  useEffect(() => {
    setSearchTerm(searchTermQuery);
  }, [searchTermQuery]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [searchTerm, debounceMs]);

  const fullQueryKey = useMemo(
    () => [...queryKey, debouncedSearchTerm, pageSize],
    [queryKey, debouncedSearchTerm, pageSize],
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isError,
    error,
    refetch,
  } = useInfiniteQuery(
    fullQueryKey,
    async ({ signal, pageParam = 0 }) => {
      if (handlers?.beforeQuery) handlers.beforeQuery();
      return fetchPage({
        pageParam,
        pageSize,
        debouncedSearchTerm,
        signal,
      });
    },
    {
      enabled,
      getNextPageParam: (lastPage, allPages) =>
        lastPage && lastPage.length === pageSize ? allPages.length : undefined,
      keepPreviousData: true,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      onError: handlers?.onError,
      onSuccess: handlers?.onSuccess,
      onSettled: handlers?.onSettled,
    },
  );

  const flat = data?.pages ? ([] as TItem[]).concat(...data.pages) : undefined;

  return [
    flat,
    setSearchTerm,
    {
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      isFetching,
      isError,
      error,
      refetch,
    },
  ];
};
